'use client';

import { ChartConfig, ChartContainer, Cell, Pie, PieChart } from '@ophelos/ui/components/chart';
import { Label, PolarGrid, PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts';
import { useTranslation } from '@/hooks/useTranslation';
import { CurrencyDisplay } from './CurrencyDisplay';

interface PieChartSegment {
  creditor: string;
  amount: number;
  percentage: number;
}

interface PaymentPieChartProps {
  totalAmount: number;
  payingAmount: number;
  segments?: PieChartSegment[]; // For multiple creditors
  currency?: string;
}

// Customer portal exposes chart-1..chart-5 only (see DESIGN.md §3).
const COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)'];

export function PaymentPieChart({ totalAmount, payingAmount, segments, currency = 'EUR' }: PaymentPieChartProps) {
  const { t } = useTranslation();
  const percentage = (payingAmount / totalAmount) * 100;

  // No segments (case-level single debt) — radial gauge showing coverage of the balance.
  // A single account segment still renders as a donut below (one full ring).
  if (!segments || segments.length === 0) {
    // The bar spans the chart's whole angular range, so the share paid is expressed through
    // the end angle; the full ring underneath is drawn by the polar grid at the bar's radii.
    const endAngle = 90 + (Math.min(100, Math.max(0, percentage)) / 100) * 360;
    const chartData = [
      {
        category: 'payment',
        amount: payingAmount,
        fill: 'var(--chart-1)',
      },
    ];

    const chartConfig = {
      amount: {
        label: 'Amount',
      },
      payment: {
        label: 'Payment',
        color: 'var(--chart-1)',
      },
    } satisfies ChartConfig;

    return (
      <div className="flex flex-col items-center gap-4">
        <ChartContainer config={chartConfig} className="h-[160px] aspect-square">
          <RadialBarChart data={chartData} startAngle={90} endAngle={endAngle} innerRadius={58} outerRadius={74} cx="50%" cy="50%">
            <PolarGrid gridType="circle" radialLines={false} stroke="none" className="first:fill-muted last:fill-background" polarRadius={[74, 58]} />
            <RadialBar dataKey="amount" cornerRadius={8} />
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                        <tspan x={viewBox.cx} y={(viewBox.cy || 0) - 5} className="fill-foreground text-2xl font-bold">
                          {percentage.toFixed(0)}%
                        </tspan>
                        <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 18} className="fill-muted-foreground text-sm">
                          {t('paymentTabs.ofBalance')}
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </PolarRadiusAxis>
          </RadialBarChart>
        </ChartContainer>
      </div>
    );
  }

  // Multiple creditors — donut with one slice per case, sized by its allocated
  // amount. Recharts' RadialBar donut rendered blank for concentric bars over a
  // partial sweep; a Pie draws a real slice per datum.
  const chartData = segments.map((segment, index) => ({
    creditor: segment.creditor,
    amount: segment.amount,
    percentage: segment.percentage,
    fill: COLORS[index % COLORS.length],
  }));

  const chartConfig = segments.reduce(
    (config, segment, index) => ({
      ...config,
      [segment.creditor.toLowerCase().replace(/\s+/g, '_')]: {
        label: segment.creditor,
        color: COLORS[index % COLORS.length],
      },
    }),
    {
      amount: { label: 'Amount' },
    } as ChartConfig,
  );

  return (
    <div className="flex flex-col items-center gap-4">
      <ChartContainer config={chartConfig} className="h-[160px] aspect-square">
        <PieChart>
          <Pie
            data={chartData}
            dataKey="amount"
            nameKey="creditor"
            innerRadius={50}
            outerRadius={80}
            paddingAngle={2}
            isAnimationActive={false}
          >
            {chartData.map((segment) => (
              <Cell key={segment.creditor} fill={segment.fill} stroke="var(--background)" />
            ))}
            <Label
              content={({ viewBox }) => {
                if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                  return (
                    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                      <tspan x={viewBox.cx} y={(viewBox.cy || 0) - 5} className="fill-foreground text-2xl font-bold">
                        {percentage.toFixed(0)}%
                      </tspan>
                      <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 18} className="fill-muted-foreground text-sm">
                        {t('paymentTabs.ofBalance')}
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </Pie>
        </PieChart>
      </ChartContainer>

      {/* Legend for multiple creditors */}
      <div className="flex flex-col gap-2 w-full max-w-xs">
        {segments.map((segment, index) => (
          <div key={segment.creditor} className="flex items-center justify-between text-sm" data-testid="pie-legend-row">
            <div className="flex items-center gap-2">
              <div className="size-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
              <span className="text-muted-foreground">{segment.creditor}</span>
            </div>
            <div className="flex items-center gap-2">
              <CurrencyDisplay amount={segment.amount} currency={currency} className="text-sm font-semibold" />
              <span className="text-xs text-muted-foreground">({segment.percentage.toFixed(1)}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
